/**
 * useWorkspaceStrategies — the workspace's strategies of every kind, loaded.
 *
 * Reads the five strategy stores and asks each to load when the hook is first
 * enabled. Callers get one list (see `workspaceStrategies.ts`).
 */

import { useEffect, useMemo } from 'react';
import { useStrategyStore } from '@/stores/prioritization/strategyStore';
import { useOSTStore } from '@/stores/prioritization/ostStore';
import { useStrategyKernelStore } from '@/stores/prioritization/strategyKernelStore';
import { useStrategyCanvasStore } from '@/stores/prioritization/strategyCanvasStore';
import { useV2momStore } from '@/stores/prioritization/v2momStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { buildWorkspaceStrategyOptions, type WorkspaceStrategyOption } from './workspaceStrategies';

export function useWorkspaceStrategies(enabled = true): WorkspaceStrategyOption[] {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);

  const classicMap = useStrategyStore((s) => s.strategies);
  const ostTrees = useOSTStore((s) => s.trees);
  const kernelMap = useStrategyKernelStore((s) => s.kernels);
  const canvases = useStrategyCanvasStore((s) => s.canvases);
  const v2moms = useV2momStore((s) => s.v2moms);

  useEffect(() => {
    if (!enabled || !workspaceId) return;
    void useStrategyStore.getState().fetchFromSupabase();
    void useOSTStore.getState().fetchTrees(workspaceId);
    void useStrategyKernelStore.getState().fetchFromSupabase();
    void useStrategyCanvasStore.getState().fetchCanvases();
    void useV2momStore.getState().fetchV2moms();
  }, [enabled, workspaceId]);

  return useMemo(
    () =>
      enabled
        ? buildWorkspaceStrategyOptions({
            classic: Object.values(classicMap ?? {}),
            ostTrees,
            kernels: Object.values(kernelMap ?? {}),
            canvases,
            v2moms,
          })
        : [],
    [enabled, classicMap, ostTrees, kernelMap, canvases, v2moms],
  );
}
